import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconActionFilter } from "@/assets/icons/figma/IconActionFilter";
import { IconNavigationCross } from "@/assets/icons/figma/IconNavigationCross";
import { FacetPanel } from "@/features/search/components/facets/FacetPanel";
import { FacetSection } from "@/features/search/components/facets/FacetSection";
import { SortRadioGroup } from "@/features/search/components/sort/SortRadioGroup";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { useSort } from "@/features/search/hooks/useSort";
import { formatResultCount } from "@/features/search/utils/count-placeholder.utils";
import {
  getAppliedFilterCount,
  getVisibleFacets,
} from "@/features/search/utils/facets.utils";
import { Button } from "@/ui/cva/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@radix-ui/react-dialog";

/**
 * Mobile filters and sort: a slide-in panel over the results. Reuses `FacetPanel`
 * verbatim — only the chrome differs from desktop, so the facet rendering stays
 * in one place. Filtering applies immediately (the panel stays open); the
 * footer button just dismisses it.
 *
 * Sort is the first section: below `md` the control row has no room for a second control, so the
 * panel owns sorting as well as filtering — which is what its CTA says.
 */
export const FacetPanelMobile = () => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { query, navigation } = useSearchContext();
  const { locked: sortLocked } = useSort();

  const total = query.data?.pageInfo.total ?? 0;
  /* Locked filters are left out: they are the page, not a refinement, and the panel behind this
   * badge offers no way to clear them — so counting them was a number with nothing to undo. */
  const appliedCount = getAppliedFilterCount(
    getVisibleFacets(query.data?.facets ?? [])
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          color="brand"
          appearance="outlined"
          size="small"
          /* Spans the whole control row, on a line of its own below `md`. */
          fill
          leftIcon={<IconActionFilter className="h-4 w-4 shrink-0" />}
          rightIcon={
            appliedCount > 0 ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1 text-xs font-bold text-white">
                {appliedCount}
              </span>
            ) : undefined
          }
        >
          {t("listing:filter-sort-and-filter")}
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-[1000] bg-black/40 data-[state=closed]:animate-dialog-overlay-hide data-[state=open]:animate-dialog-overlay-show" />
        <DialogContent
          aria-describedby={undefined}
          className="fixed inset-y-0 left-8 right-0 z-[1000] flex flex-col bg-white data-[state=closed]:animate-menu-mobile-hide data-[state=open]:animate-menu-mobile-show"
        >
          <div className="flex items-center space-x-2 border-b border-grey-200 p-4">
            <DialogTitle className="flex-1 font-title text-lg font-semibold text-black">
              {t("listing:filter-sort-and-filter")}
            </DialogTitle>
            <DialogClose aria-label={t("common:common-close")}>
              <IconNavigationCross className="h-6 w-6 text-black" />
            </DialogClose>
          </div>

          <div className="flex-1 overflow-y-auto px-4">
            {/* Reuses the facet section chrome so sorting reads as the panel's first filter. */}
            {!sortLocked && (
              <FacetSection title={t("common:common-sort-by")} defaultOpen>
                <SortRadioGroup />
              </FacetSection>
            )}
            <FacetPanel />
          </div>

          <div className="grid grid-cols-2 gap-2 border-t border-grey-200 p-4">
            <Button
              appearance="outlined"
              size="small"
              fill
              className="py-3"
              onClick={() => navigation.reset()}
            >
              {t("listing:filter-clear-all")}
            </Button>
            <DialogClose asChild>
              <Button size="small" fill>
                {t("listing:filter-show-results", {
                  count: total,
                  replace: { count: formatResultCount(total, locale) },
                })}
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
